import { Controller } from "@hotwired/stimulus"

// Job form location field: Google Places autocomplete or Mapbox geocoder.
export default class extends Controller {
  static targets = ["location", "lat", "lng"]
  static values = {
    useMapbox: Boolean,
    apiKey: String,
    accessToken: String,
  }

  connect() {
    this.abortController = new AbortController()
    this.initialize()
  }

  disconnect() {
    this.abortController.abort()
    this.teardownMapboxGeocoder()
  }

  initialize() {
    if (this.useMapboxValue) {
      this.initializeMapboxGeocoder()
    } else {
      this.initializeGoogleAutocomplete()
    }
  }

  initializeGoogleAutocomplete() {
    if (typeof google === "undefined") {
      const script = document.createElement("script")
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&libraries=places`
      script.async = true
      script.defer = true
      script.onload = () => this.setupGoogleAutocomplete()
      document.head.appendChild(script)
    } else {
      this.setupGoogleAutocomplete()
    }
  }

  setupGoogleAutocomplete() {
    if (this.abortController.signal.aborted) return

    const autocomplete = new google.maps.places.Autocomplete(this.locationTarget, {
      types: ["address"],
      componentRestrictions: { country: ["us"] },
    })

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace()
      if (!place.geometry) {
        alert(`No details available for input: ${place.name}`)
        return
      }

      this.latTarget.value = place.geometry.location.lat()
      this.lngTarget.value = place.geometry.location.lng()
    })

    this.locationTarget.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Enter") event.preventDefault()
      },
      { signal: this.abortController.signal }
    )
  }

  async initializeMapboxGeocoder() {
    const manager = window.mapboxManager
    if (!manager) {
      console.error("MapboxManager not available")
      return
    }

    if (!this.accessTokenValue) {
      console.error("Mapbox access token not provided")
      return
    }

    await manager.loadAPI(this.accessTokenValue)
    if (this.abortController.signal.aborted) return

    const dummyMap = {
      getCenter: () => ({ lng: 0, lat: 0, wrap: () => ({ lng: 0, lat: 0 }) }),
      getZoom: () => 10,
      flyTo() {},
      on() {
        return this
      },
      off() {
        return this
      },
      getBounds: () => ({
        extend: () => {},
        getNorth: () => 0,
        getSouth: () => 0,
        getEast: () => 0,
        getWest: () => 0,
      }),
      loaded: () => true,
      fire: () => {},
      _container: document.createElement("div"),
    }

    const geocoder = await manager.createGeocoder({
      accessToken: this.accessTokenValue,
      mapboxgl: window.mapboxgl,
      placeholder: this.locationTarget.placeholder || "Search for an address",
      countries: "us",
      types: "address,place,region",
      marker: false,
      flyTo: false,
    })

    if (this.abortController.signal.aborted) return

    const geocoderContainer = geocoder.onAdd(dummyMap)
    geocoderContainer.className = `${geocoderContainer.className || ""} mapbox-geocoder-container`
    this.locationTarget.parentNode.insertBefore(geocoderContainer, this.locationTarget.nextSibling)
    this.locationTarget.style.display = "none"
    this.geocoderContainer = geocoderContainer
    this.geocoder = geocoder

    geocoder.on("result", (event) => {
      const result = event.result
      if (result.place_name) {
        this.locationTarget.value = result.place_name
      } else if (result.text) {
        const addressParts = [result.text]
        result.context?.forEach((item) => {
          if (["place", "district", "locality", "region", "postcode"].includes(item.id.split(".")[0])) {
            addressParts.push(item.text)
          }
        })
        this.locationTarget.value = addressParts.join(", ")
      }

      if (result.center?.length === 2) {
        this.lngTarget.value = result.center[0]
        this.latTarget.value = result.center[1]
      }

      this.locationTarget.dispatchEvent(new Event("change", { bubbles: true }))
      this.latTarget.dispatchEvent(new Event("change", { bubbles: true }))
      this.lngTarget.dispatchEvent(new Event("change", { bubbles: true }))
    })

    geocoder.on("error", (event) => {
      console.error("Geocoder error:", event.error)
    })

    geocoder.on("clear", () => {
      this.locationTarget.value = ""
      this.latTarget.value = ""
      this.lngTarget.value = ""
    })

    if (this.locationTarget.value) {
      geocoder.setInput(this.locationTarget.value)
    }
  }

  teardownMapboxGeocoder() {
    if (this.geocoderContainer) {
      this.geocoderContainer.remove()
      this.geocoderContainer = null
    }
    this.geocoder = null
    if (this.hasLocationTarget) {
      this.locationTarget.style.display = ""
    }
  }
}
